import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { valueOrDefault } from "chart.js/helpers";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState } from "react";
import { ComponentLoader } from "../loader/componentLoader";
import { useDispatch } from "react-redux";
import {
  getDataList,
  setListingData,
  setTemplateData,
} from "../../store/listing/listingSlice";
import { getConfiguredLabel, convertStringToTitleCase } from "../../common/commonHelper";
import { DocumentsPage, graphFieldName, other } from "../../common/labelConstants";
import { json } from "d3";

// Note: changes to the plugin code is not reflected to the chart, because the plugin is loaded at chart construction time and editor changes only trigger an chart.update().
const image = new Image();
image.src = "https://www.chartjs.org/img/chartjs-logo.svg";

const plugin = {
  id: "custom_canvas_background_image",
  beforeDraw: function (chart) {
    if (chart?.config?.options?.elements?.center) {
      // Get ctx from string
      const ctx = chart.ctx;
      // Get options from the center object in options
      const centerConfig = chart.config.options.elements.center;
      const fontStyle = centerConfig.fontStyle || "Arial";
      const txt = centerConfig.text;
      let txt2 = centerConfig.text2;
      const color = centerConfig.color || "#000";
      const maxFontSize = centerConfig.maxFontSize || 35;
      const sidePadding = centerConfig.sidePadding || 20;
      const sidePaddingCalculated =
        (sidePadding / 100) * (chart.innerRadius * 2);

      // Start with a base font of 30px
      ctx.font = "12px " + fontStyle;
      // Get the width of the string and also the width of the element minus 10 to give it 5px side padding
      const stringWidth = ctx.measureText(txt).width;
      const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;
      // Find out how much the font can grow in width.
      const widthRatio = elementWidth / stringWidth;
      const newFontSize = Math.floor(30 * widthRatio);
      const elementHeight = chart.innerRadius * 2;
      // Pick a new font size so it will not be larger than the height of label.
      const fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);
      const minFontSize = centerConfig.minFontSize;
      const lineHeight = centerConfig.lineHeight || 25;
      const wrapText = false;
      if (minFontSize === undefined) {
        minFontSize = 20;
      }
      if (minFontSize && fontSizeToUse < minFontSize) {
        fontSizeToUse = minFontSize;
        wrapText = true;
      }

      // Set font settings to draw it correctly.
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2 - 20;

      ctx.fillStyle = color;
      if (!wrapText) {
        ctx.font = "2vh " + fontStyle;
        ctx.fillStyle = "#868686";
        ctx.fillText(txt, centerX, centerY);
        ctx.fillStyle = "#2c2c2c";
        ctx.font = "bold 4vh " + fontStyle;
        const height = ctx.measureText("M").width;
        ctx.fillText(txt2, centerX, centerY + height);
        return;
      }
      const words = txt.split(" ");
      const line = "";
      const lines = [];

      // Break words up into multiple lines if necessary
      for (const n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > elementWidth && n > 0) {
          lines.push(line);
          line = words[n] + " ";
        } else {
          line = testLine;
        }
      }

      // Move the center up depending on line height and number of lines
      centerY -= (lines.length / 2) * lineHeight;
      for (var n = 0; n < lines.length; n++) {
        ctx.fillText(lines[n], centerX, centerY);
        centerY += lineHeight;
      }
      //Draw text in center
      ctx.fillText(line, centerX, centerY);
    }
  },
};

if(!window.doughnut){
  ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, plugin);
  window.doughnut = true
}

const _props = {
  labels: [],
  datasets: [],
  totalDatasets: 0,
  usePercentNotation: true,
  centerTitle: "",
  legendLabel: "",
  graphConfig:{},
  clickFilter: () => {},
  legendFilter:()=>{}
};

export function DoughnutChart(props = _props) {
  const sortedData = props.labels.map((key, index) => ({ key, value: props.datasets[index] })).sort((a, b) => b.value - a.value);
  const sortedLabels = sortedData.map((item) => typeof item.key==='object'?other:item.key);
  const sortedDatasets = sortedData.map((item) => item.value);

  const [legend, setLegend] = useState([]);
  const data = {
    labels: sortedLabels,
    datasets: [
      {
        data: sortedDatasets,
        backgroundColor: props.graphConfig.colorPalette,
        hoverOffset: 4,
        options:{

        }
      },
    ],
  };


  const doughnut = (
    <Doughnut
      options={{
      

        plugins: {
          datalabels: {
            borderColor: "white",
            borderRadius: 25,
            borderWidth: 0,
            color: "white",
            display: true,
            backgroundColor: (context) => {
              const dataset = context.dataset;
              const value = dataset.data[context.dataIndex];
              const percentage = Math.round(
                (value / props.totalDatasets) * 100
              );
              const color = legend[context.dataIndex]
                ? legend[context.dataIndex].fill
                : "";
              return percentage < 10 ? color : '';
            },
            display:(context)=>{
              const dataset = context.dataset;
              const value = dataset.data[context.dataIndex];
              const percentage = Math.round(
                (value / props.totalDatasets) * 100
              );
              return percentage>1 ? true:false;
            },
            borderRadius: 3,
            font: {
              weight: "normal",
            },
            padding: 2,

            formatter: (val) => {
              if (props.graphConfig.usePercent) {
                const v = Math.round((val / props.totalDatasets) * 100);
                  return v + "%";
              } else return Math.round(val);
            },
          },
          
          

          legend: {
            display: false,
            position: "right",
            labels: {
              color: "#333333",
              boxWidth: 15,
              generateLabels: function (chart) {
                let leg = [];
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  return data.labels.map(function (label, i) {
                    const meta = chart.getDatasetMeta(0);
                    const ds = data.datasets[0];
                    const arc = meta.data[i];
                    const custom = (arc && arc.custom) || {};
                    const getValueAtIndexOrDefault = valueOrDefault;
                    const arcOpts = chart.options.elements.arc;
                    const fill = custom.backgroundColor
                      ? custom.backgroundColor
                      : getValueAtIndexOrDefault(
                          ds.backgroundColor,
                          i,
                          arcOpts.backgroundColor
                        );
                    const stroke = custom.borderColor
                      ? custom.borderColor
                      : getValueAtIndexOrDefault(
                          ds.borderColor,
                          i,
                          arcOpts.borderColor
                        );
                    // var bw = custom.borderWidth ? custom.borderWidth : getValueAtIndexOrDefault(ds.borderWidth, i, arcOpts.borderWidth);
                   
                  
                    // const value = chart.config.data.datasets[0].data[arc.$context.index];
                    const value = ds.data[i];
                    leg.push({
                      label: label,

                      value: value,

                      fill: fill[i],
                    });
                    
                    if (legend.length < leg.length) setLegend(leg); 
                    return {
                      text: label + " : " + value,
                      fillStyle: fill[i],
                      // strokeStyle: stroke,
                      // lineWidth: bw,
                      hidden: isNaN(ds.data[i]) || meta.data[i].hidden,
                      index: i,
                    };
                  });
                } else {
                  return [];
                }
              },
            },
            //
          },
        },
        
        

        // Core options
        responsive: true,
        cutout: "70%",
        transitions: 20,
        layout: {
          padding: 10,
        },
        elements: {
          line: {
            fill: true,
          },
          point: {
            hoverRadius: 7,
            radius: 5,
            
          },
          center: {
            text2: props.totalDatasets > 0 ? props.totalDatasets : "",
            text: props.totalDatasets > 0 ? convertStringToTitleCase(props.graphConfig.centerTitle) : "",
            color: "#000", // Default is #000000
            fontStyle: "FSAlbert-Bold", // Default is Arial
            sidePadding: 20, // Default is 20 (as a percentage)
            minFontSize: 2, // Default is 20 (in px), set to false and text will not wrap.
            lineHeight: 25, // Default is 25 (in px), used for when text wraps
          },
        },
        onClick: (e, activeEls) => {
          //console.log(data);
          props.clickFilter(e, activeEls, data);
        },
        onHover:(e,activeEls)=>{
          //console.log(e);
          e.native.target.style.cursor = activeEls[0] ? 'pointer':'';
        }
      }}
      data={data}
    />
  );

  return (
    <>
      {doughnut ? (
        
        <div className="row align-item-center">
          <div className="col-md-6 doughnut_chart">{doughnut}</div>
          <div className="col-md-6 rc_doughnut_legends_wrapper">
            {legend && legend.map((x, index) => {
              return (
                <li
                  key={index}
                  className="legendTitle"
                  onClick={()=>props.legendFilter(x.label)}
                >
                  <span
                    className="legendBoxColor"
                    style={{ backgroundColor: x.fill }}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  <span className="legendText">
                    {" "}
                    {x.label}
                    <strong>
                      {" "}
                      {"("}
                      <span>
                        {x.value}
                        {` ${convertStringToTitleCase(props.graphConfig.label)})`}
                      </span>
                    </strong>
                  </span>
                </li>
              );
            })}
             <div className="slb-chart-text mt-3">
            <p><strong>*</strong>% may not add upto 100 due to rounding off</p>
          </div>
          </div>
         
        </div>
      ) : (
        <ComponentLoader />
      )}
    </>
  );
}
